import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, of, Subscription, Observer, EMPTY } from 'rxjs';
import { mergeMap, tap, catchError, takeUntil, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Query } from '../../models/analytics-api/query.model';
import { QueryResult } from '../../models/analytics-api/query_result.model';
import { AnalyticsApiService } from '../../services/analytics-api/analytics-api.service';
import { RequestOptionsArgs } from '../backend/backend.service';

@Injectable({
  providedIn: 'root',
})
export class WidgetQueueProxyService implements OnDestroy {
  private queryQueue$: Subject<{
    query: Query;
    options: RequestOptionsArgs;
    subscriber: Observer<QueryResult>;
  }>;

  private cancelRequests$ = new Subject<void>();
  private processingSubscription: Subscription;
  private MAX_CONCURRENT_QUEUE = 5;
  private queueSize$ = new Subject<number>();
  private queueSizeSubscription: Subscription;
  private activeQueriesCount = 0;
  private queueEmpty$ = new Subject<void>();

  constructor(
    private analyticsApi: AnalyticsApiService,
  ) {
    this.initProcessingSubscription();
    this.initQueueSizeTracking();
  }

  private initProcessingSubscription() {
    this.queryQueue$ = new Subject<{
      query: Query;
      options: RequestOptionsArgs;
      subscriber: Observer<QueryResult>;
    }>();

    this.processingSubscription = this.queryQueue$
      .pipe(
        takeUntil(this.cancelRequests$),
        mergeMap(
          ({ query, options, subscriber }) => {
            this.activeQueriesCount++;
            this.queueSize$.next(this.activeQueriesCount);
            return this.analyticsApi.runQuery(query, options).pipe(
              takeUntil(this.cancelRequests$),
              tap({
                next: (result) => {
                  subscriber.next(result);
                  subscriber.complete();
                  this.activeQueriesCount--;
                  this.queueSize$.next(this.activeQueriesCount);
                },
                error: (error) => {
                  subscriber.error(error);
                  this.activeQueriesCount--;
                  this.queueSize$.next(this.activeQueriesCount);
                },
              }),
              catchError(() => {
                return EMPTY;
              }),
            );
          },
          this.MAX_CONCURRENT_QUEUE,
        ),
      )
      .subscribe();
  }

  private initQueueSizeTracking() {
    this.queueSizeSubscription = this.queueSize$.pipe(
      distinctUntilChanged(),
      debounceTime(5000),
    ).subscribe(size => {
      if (size === 0) {
        this.queueEmpty$.next();
      }
    });
  }

  analiticsQueueProxy(
    query: Query,
    options: RequestOptionsArgs,
  ): Observable<QueryResult> {
    if (options.cache) {
      const cachedQueryResult = this.analyticsApi.runQueryCache(query, options);
      if (!!cachedQueryResult) {
        return of(cachedQueryResult);
      }
    }

    return new Observable<QueryResult>((subscriber) => {
      this.queryQueue$.next({ query, options, subscriber });
    }).pipe(
      takeUntil(this.cancelRequests$),
    );
  }

  public onQueueEmpty(): Observable<void> {
    this.queueSize$.next(this.activeQueriesCount);
    return this.queueEmpty$.asObservable();
  }

  public cancelQueryQueue() {
    if (this.processingSubscription) {
      this.processingSubscription.unsubscribe();
    }

    this.cancelRequests$.next();
    this.cancelRequests$ = new Subject<void>();
    this.activeQueriesCount = 0;
    this.queueSize$.next(this.activeQueriesCount);
    this.initProcessingSubscription();
  }

  ngOnDestroy() {
    this.processingSubscription.unsubscribe();
    this.queueSizeSubscription.unsubscribe();
    this.cancelRequests$.next();
    this.cancelRequests$.complete();
    this.queryQueue$.complete();
    this.queueSize$.complete();
    this.queueEmpty$.complete();
  }
}
